import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChooseWhenBookBannerVariant,
  DiningFlowEventType,
  DiningReservationMode,
  DiningStepId,
} from '@msc-b2c/interfaces/enums';
import {
  UseDiningPaxEditNavigationParams,
  UseDiningPaxEditNavigationResult,
} from '@msc-b2c/interfaces/interfaces';

/**
 * Encapsulates pax-edit behavior for CART and ALREADY_PURCHASED flows.
 *
 * Why extracted:
 * - Keeps step-rendering component focused on UI composition.
 * - Isolates edit transition rules (baseline snapshot, change detection,
 *   branching next/back transitions) in one place.
 */
export const useDiningPaxEditNavigation = ({
  currentStep,
  isCartOrAlreadyPurchasedFlow,
  passengersToAdd,
  preserveSelectionGoingBack,
  selectedRestaurantsCount,
  serializePassengersSelection,
  prefetchINT135DiningRoomMetadata,
  resetReservationsForPassengerEdit,
  onReservationModeSelected,
  dispatch,
  setPassengersToAdd,
  setChooseWhenBookBannerVariant,
  setHasAttemptedDateTimeBootstrap,
  setIsDiningButtonLoading,
}: UseDiningPaxEditNavigationParams): UseDiningPaxEditNavigationResult => {
  /**
   * Snapshot of passengers at edit entry time.
   * Used to hide confirm CTA until user makes a real change.
   */
  const [paxEditSelectionBaselineSerialized, setPaxEditSelectionBaselineSerialized] = useState<
    string | null
  >(null);

  const isPaxSelectionEditStep = currentStep === DiningStepId.PAX_SELECTION_EDIT;
  const isCartOrAlreadyPurchasedPaxEditStep =
    isCartOrAlreadyPurchasedFlow && isPaxSelectionEditStep;

  /**
   * Clear baseline when user exits edit step, so next edit starts fresh.
   */
  useEffect(() => {
    if (!isPaxSelectionEditStep) {
      setPaxEditSelectionBaselineSerialized(null);
    }
  }, [isPaxSelectionEditStep]);

  /**
   * In edit mode, confirm CTA stays hidden until passenger selection differs
   * from the captured baseline.
   */
  const hidePaxEditConfirmUntilSelectionChanges = useMemo(
    () =>
      isCartOrAlreadyPurchasedFlow &&
      isPaxSelectionEditStep &&
      paxEditSelectionBaselineSerialized !== null &&
      serializePassengersSelection(passengersToAdd) === paxEditSelectionBaselineSerialized,
    [
      isCartOrAlreadyPurchasedFlow,
      isPaxSelectionEditStep,
      paxEditSelectionBaselineSerialized,
      passengersToAdd,
      serializePassengersSelection,
    ]
  );

  /**
   * Starts pax-edit flow.
   * - captures baseline
   * - optionally clears current selection
   * - fires INT135 prefetch to reduce wait time on confirm
   */
  const handleEditPassengers = useCallback(() => {
    const baselineSnapshot = serializePassengersSelection(passengersToAdd);
    setPaxEditSelectionBaselineSerialized(baselineSnapshot);

    if (!preserveSelectionGoingBack) {
      setPassengersToAdd(null);
    }

    void prefetchINT135DiningRoomMetadata();
    dispatch({ type: DiningFlowEventType.EDIT_PAX });
  }, [
    dispatch,
    passengersToAdd,
    prefetchINT135DiningRoomMetadata,
    preserveSelectionGoingBack,
    serializePassengersSelection,
    setPassengersToAdd,
  ]);

  /**
   * Resolves next action from pax step.
   *
   * Edit mode:
   * - no selection change: no-op
   * - with reservations: reset reservations and move forward to rebuild date/time
   * - without reservations: go back to recap step
   *
   * Standard mode:
   * - plain NEXT transition
   */
  const handlePaxSelectionNext = useCallback(async () => {
    setChooseWhenBookBannerVariant(ChooseWhenBookBannerVariant.NONE);
    if (isPaxSelectionEditStep) {
      await prefetchINT135DiningRoomMetadata();
      const selectionChanged =
        paxEditSelectionBaselineSerialized !== null &&
        serializePassengersSelection(passengersToAdd) !== paxEditSelectionBaselineSerialized;

      if (!selectionChanged) {
        setIsDiningButtonLoading(false);
        return;
      }

      setHasAttemptedDateTimeBootstrap(false);
      if (selectedRestaurantsCount > 0) {
        resetReservationsForPassengerEdit();
        dispatch({
          type: DiningFlowEventType.SELECT_RESERVATION_MODE,
          mode: DiningReservationMode.WITH_TIMESLOT,
        });
        onReservationModeSelected?.(DiningReservationMode.WITH_TIMESLOT);
        dispatch({ type: DiningFlowEventType.NEXT });
        setIsDiningButtonLoading(false);
        return;
      }

      dispatch({ type: DiningFlowEventType.BACK });
      setIsDiningButtonLoading(false);
      return;
    }

    dispatch({ type: DiningFlowEventType.NEXT });
    setIsDiningButtonLoading(false);
  }, [
    dispatch,
    isPaxSelectionEditStep,
    onReservationModeSelected,
    passengersToAdd,
    paxEditSelectionBaselineSerialized,
    prefetchINT135DiningRoomMetadata,
    resetReservationsForPassengerEdit,
    selectedRestaurantsCount,
    serializePassengersSelection,
    setChooseWhenBookBannerVariant,
    setHasAttemptedDateTimeBootstrap,
    setIsDiningButtonLoading,
  ]);

  return {
    isPaxSelectionEditStep,
    isCartOrAlreadyPurchasedPaxEditStep,
    hidePaxEditConfirmUntilSelectionChanges,
    handleEditPassengers,
    handlePaxSelectionNext,
  };
};
